/**
 * Server error codes → player-facing words.
 *
 * Branching on `code` rather than on `message` is what makes this translatable
 * at all: the server's messages are English prose written for an operator's
 * logs, and showing them to an Amharic-speaking player would be a silent
 * fallback to the wrong language. The code is a stable contract; the words are
 * ours.
 */

import { ApiError } from './client.ts';
import type { Messages } from '../i18n/messages.ts';

export function errorMessage(err: unknown, t: Messages): string {
  if (!(err instanceof ApiError)) return t.errorGeneric;

  switch (err.code) {
    case 'insufficient_funds':
      return t.errorInsufficientFunds;
    case 'round_full':
      return t.errorRoundFull;
    case 'card_limit_reached':
      return t.errorCardLimit;
    case 'invalid_round_state':
      return t.errorRoundState;
    case 'not_found':
      return t.errorNotFound;
    case 'rate_limited':
      return t.errorRateLimited;
    case 'account_not_active':
      return t.errorAccountNotActive;
    case 'network_error':
      return t.offline;

    case 'daily_deposit_cap':
      // One message for both limits. The player's recovery is the same either
      // way — wait — and naming which ceiling they hit would tell someone
      // probing the limits exactly how the controls are shaped.
      return t.errorDailyDepositCap;
    case 'withdrawal_velocity':
      // Same reasoning, on the way out.
      return t.errorWithdrawalLimit;
    case 'bonus_not_withdrawable':
      // Migration 066: promotional money must be played, not withdrawn.
      return t.errorBonusNotWithdrawable;
    default:
      // Including `internal_error`, whose message is deliberately generic
      // server-side and carries nothing worth showing.
      return t.errorGeneric;
  }
}
